import React, { ChangeEvent, useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { computeText } from '@/locales/utils';
import Wizard from '@/components/atoms/Wizard';
import { getChannelText } from '@/utils/innovorder/channel';
import { SVGIcon, SVGIcons } from '@/components/atoms/Icon/SVGIcon';
import RawModal from '@/components/atoms/RawModal';
import { theme } from '@/core/theme';
import { QueryError, stringifyError } from '@/utils/errors';
import { ChannelId } from '@/services/innovorder/channel/channel.type';
import { usePublishMenuOnChannelMutation } from '@/services/innovorder/menu/menu.endpoints';
import { Menu, MenuChannel, MenuPosDevice } from '@/services/innovorder/menu/menu.type';
import { getUserToken } from '@/redux/user';
import { useKioskReloadMutation } from '@/services/innovorder/restaurant/restaurant.endpoints';
import { Checkbox } from '@/components/atoms/Checkbox';
import { MarketplaceConfig } from '@/services/innovorder/marketplace/marketplace.type';
import { getLaunchDarklyState } from '@/redux/launchDarkly';
import { MenuModalChannelsTitle, MenuModalInputContainer } from '../MenuModal.style';
import {
    AssociatedChannelsContainer,
    AssociatedChannelsDescription,
    AssociatedChannelsTitle,
    DeviceListContainer,
    MenuDescriptionContainer,
    MenuTitleContainer,
} from './PublishMenuModal.style';

export type PublishMenuModalProps = {
    menuId: number;
    menu: Menu;
    channels: MenuChannel[];
    posDevices: MenuPosDevice[];
    menuName: string | null;
    restaurantId?: number;
    onHide: () => void;
};

export const formatExtraData = (
    extraData: Record<string, unknown> | undefined,
    intl: { formatMessage: (descriptor: { id: string }, values: Record<string, unknown>) => string },
): string => {
    if (!extraData || !extraData.duplicateBarCodes || typeof extraData.duplicateBarCodes !== 'object') {
        return '';
    }

    const duplicateBarCodes = extraData.duplicateBarCodes as Record<string, string[]>;
    const messages: string[] = [];

    Object.entries(duplicateBarCodes).forEach(([barCode, products]) => {
        if (Array.isArray(products) && products.length > 0) {
            messages.push(
                intl.formatMessage(
                    { id: 'menu.modal.publish.duplicateBarCode' },
                    { barCode, products: products.join(', ') },
                ),
            );
        }
    });

    return messages.join('.');
};

export const stringifyPublishError = (
    error: QueryError,
    intl: { formatMessage: (descriptor: { id: string }) => string },
): string => {
    const baseMessage = stringifyError(error);

    if (!error || !('data' in error)) {
        return baseMessage;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const data = error.data as any;
    const extraDataMessage = formatExtraData(data?.extraData, intl);

    return extraDataMessage ? `${baseMessage} ${extraDataMessage}` : baseMessage;
};

export const PublishMenuModal: React.FunctionComponent<React.PropsWithChildren<PublishMenuModalProps>> = ({
    onHide,
    menuId,
    menu,
    channels,
    posDevices,
    menuName = '',
    restaurantId,
}) => {
    const intl = useIntl();
    const token = useSelector(getUserToken);
    const [publish, setPublish] = React.useState<Record<string, boolean>>({
        [ChannelId.KIOSK]: false,
        [ChannelId.POS]: false,
        [ChannelId.DELIVEROO]: false,
        [ChannelId.UBER_EATS]: false,
    });

    const [postPublishMenu, { isSuccess: isPublishSuccess, isLoading: isPublishLoading, error: errorPublish }] =
        usePublishMenuOnChannelMutation();
    const [reloadKiosk, { isSuccess: isReloadSuccess, isLoading: isReloadLoading }] = useKioskReloadMutation();

    const handleToggleDevice = React.useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setPublish((currentPublish) => {
            return {
                ...currentPublish,
                [event.target.id]: !currentPublish[event.target.id],
            };
        });
    }, []);

    const onFormValidate = useCallback(() => {
        publish[ChannelId.POS] && postPublishMenu({ menuId: menuId as number, channelId: ChannelId.POS, token });
        publish[ChannelId.KIOSK] && reloadKiosk({ restaurantId });
        publish[ChannelId.DELIVEROO] &&
            postPublishMenu({ menuId: menuId as number, channelId: ChannelId.DELIVEROO, token });
        publish[ChannelId.UBER_EATS] &&
            postPublishMenu({ menuId: menuId as number, channelId: ChannelId.UBER_EATS, token });
    }, [postPublishMenu, reloadKiosk, menuId, token, restaurantId, publish]);

    useEffect(() => {
        if (
            (isPublishSuccess && isReloadSuccess) ||
            (isPublishSuccess && !isReloadLoading) ||
            (isReloadSuccess && !isPublishLoading)
        ) {
            onHide();
        }
    }, [isPublishSuccess, isReloadSuccess, isPublishLoading, isReloadLoading, onHide]);

    const kioskChannel = channels.find((channel) => channel.channelId === ChannelId.KIOSK);

    const marketplacesByChannelId = useMemo(() => {
        const map = new Map<ChannelId, MarketplaceConfig[]>();
        menu.moduleMarketplaces.forEach((marketplace) => {
            if (!map.has(marketplace.channelId)) {
                map.set(marketplace.channelId, []);
            }
            map.get(marketplace.channelId)?.push(marketplace);
        });
        return map;
    }, [menu]);

    return (
        <RawModal isOpen={true} onHide={onHide}>
            <Wizard
                width={520}
                errorMessage={stringifyPublishError(errorPublish, intl)}
                onValidate={onFormValidate}
                isLoading={isPublishLoading || isReloadLoading}
                steps={[
                    {
                        title: 'menu.modal.publish.title',
                        onCancel: onHide,
                        component: (
                            <MenuModalInputContainer>
                                <MenuModalChannelsTitle type="normal" weight="medium" text={menuName} />

                                {kioskChannel && (
                                    <AssociatedChannelsContainer key={`associated-channel-${kioskChannel.channelId}`}>
                                        <MenuTitleContainer>
                                            <SVGIcon icon={SVGIcons.KIOSK} size={20} color={theme.color.slate100} />
                                            <AssociatedChannelsTitle
                                                type="normal"
                                                weight="medium"
                                                text={getChannelText(intl, kioskChannel)}
                                            />
                                        </MenuTitleContainer>
                                        <MenuDescriptionContainer>
                                            <Checkbox
                                                id={kioskChannel.channelId.toString()}
                                                value={publish[kioskChannel.channelId]}
                                                onChange={handleToggleDevice}
                                            />
                                            <AssociatedChannelsDescription
                                                type="normal"
                                                weight="light"
                                                text={computeText(intl, 'menu.modal.publish.autoPublish')}
                                                isDisabled={false}
                                            />
                                        </MenuDescriptionContainer>
                                    </AssociatedChannelsContainer>
                                )}

                                {posDevices.length > 0 && (
                                    <AssociatedChannelsContainer key={`associated-channel-pos`}>
                                        <MenuTitleContainer>
                                            <SVGIcon icon={SVGIcons.POS} size={20} color={theme.color.slate100} />
                                            <AssociatedChannelsTitle type="normal" weight="medium" text={'POS'} />
                                        </MenuTitleContainer>
                                        <MenuDescriptionContainer>
                                            <Checkbox
                                                id={ChannelId.POS.toString()}
                                                value={publish[ChannelId.POS]}
                                                onChange={handleToggleDevice}
                                            />
                                            <AssociatedChannelsDescription
                                                type="normal"
                                                weight="light"
                                                text={computeText(intl, 'menu.modal.publish.devicePublish')}
                                                isDisabled={false}
                                            />
                                        </MenuDescriptionContainer>

                                        <DeviceListContainer>
                                            {posDevices.map((posDevice) => {
                                                return (
                                                    <AssociatedChannelsDescription
                                                        key={posDevice.posDeviceId}
                                                        type="normal"
                                                        weight="light"
                                                        text={'- '.concat(posDevice.deviceName)}
                                                        isDisabled={false}
                                                    />
                                                );
                                            })}
                                        </DeviceListContainer>
                                    </AssociatedChannelsContainer>
                                )}

                                {marketplacesByChannelId.get(ChannelId.DELIVEROO) && (
                                    <AssociatedChannelsContainer key={`associated-channel-deliveroo`}>
                                        <MenuTitleContainer>
                                            <SVGIcon icon={SVGIcons.DELIVEROO} size={20} color={theme.color.slate100} />
                                            <AssociatedChannelsTitle type="normal" weight="medium" text={'Deliveroo'} />
                                        </MenuTitleContainer>
                                        <MenuDescriptionContainer>
                                            <Checkbox
                                                id={ChannelId.DELIVEROO.toString()}
                                                value={publish[ChannelId.DELIVEROO]}
                                                onChange={handleToggleDevice}
                                            />
                                            <AssociatedChannelsDescription
                                                type="normal"
                                                weight="light"
                                                text={computeText(intl, 'menu.modal.publish.deliverooPublish')}
                                                isDisabled={false}
                                            />
                                        </MenuDescriptionContainer>
                                        <DeviceListContainer>
                                            {marketplacesByChannelId.get(ChannelId.DELIVEROO)?.map((marketplace) => {
                                                return (
                                                    <AssociatedChannelsDescription
                                                        key={marketplace.moduleMarketplaceId}
                                                        type="normal"
                                                        weight="light"
                                                        text={'- '.concat(
                                                            marketplace.storeDisplayName || marketplace.storeReference,
                                                        )}
                                                        isDisabled={false}
                                                    />
                                                );
                                            })}
                                        </DeviceListContainer>
                                    </AssociatedChannelsContainer>
                                )}
                                {marketplacesByChannelId.get(ChannelId.UBER_EATS) && (
                                    <AssociatedChannelsContainer key={`associated-channel-ubereats`}>
                                        <MenuTitleContainer>
                                            <SVGIcon icon={SVGIcons.UBEREATS} size={20} color={theme.color.slate100} />
                                            <AssociatedChannelsTitle type="normal" weight="medium" text={'UberEats'} />
                                        </MenuTitleContainer>
                                        <MenuDescriptionContainer>
                                            <Checkbox
                                                id={ChannelId.UBER_EATS.toString()}
                                                value={publish[ChannelId.UBER_EATS]}
                                                onChange={handleToggleDevice}
                                            />
                                            <AssociatedChannelsDescription
                                                type="normal"
                                                weight="light"
                                                text={computeText(intl, 'menu.modal.publish.ubereatsPublish')}
                                                isDisabled={false}
                                            />
                                        </MenuDescriptionContainer>
                                        <DeviceListContainer>
                                            {marketplacesByChannelId.get(ChannelId.UBER_EATS)?.map((marketplace) => {
                                                return (
                                                    <AssociatedChannelsDescription
                                                        key={marketplace.moduleMarketplaceId}
                                                        type="normal"
                                                        weight="light"
                                                        text={'- '.concat(
                                                            marketplace.storeDisplayName || marketplace.storeReference,
                                                        )}
                                                        isDisabled={false}
                                                    />
                                                );
                                            })}
                                        </DeviceListContainer>
                                    </AssociatedChannelsContainer>
                                )}
                            </MenuModalInputContainer>
                        ),
                    },
                ]}
            />
        </RawModal>
    );
};
